import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import moment from 'moment';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useTheme } from 'emotion-theming';
import Spinner from '../../../../shared/components/Spinner/Spinner';
import iconSet from '../../../../shared/images/teambuildr-selection.json';

const IconWrapperStyle = styled('div')`
  cursor: default;
  transition: all 250ms ease-in-out;
  :hover {
    opacity: 0.7;
  }
`;

const BorderedIconWrapper = styled(IconWrapperStyle)`
  border-radius: 4px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FadingIcon = styled('div')`
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const IconContainer = styled('div')`
  position: relative;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloudUploadIcon = () => {
  // Check if any saving or updating operations are in progress
  const isCalendarItemSaving = useSelector(
    (state) => state.calendar.ui.isCalendarItemSaving,
  );
  const isCalendarItemUpdating = useSelector(
    (state) => state.calendar.ui.isCalendarItemUpdating,
  );
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const calendarItems = useSelector((state) => state.calendar.data.calendarItems);
  const { colors } = useTheme();

  // Generate unique ID for tooltip
  const uploadTooltipId = 'upload-tooltip';

  // The save/loading indicator is hidden for now to improve header screen real
  // estate; flip this to bring it back. Undo still renders either way.
  const showSaveIndicator = false;

  // Get current day's calendar items using selectedDate
  // Handle both Moment objects and JavaScript Date objects
  let selectedDateString = null;
  if (selectedDate) {
    if (moment.isMoment(selectedDate)) {
      selectedDateString = selectedDate.format('YYYY-MM-DD');
    } else {
      // Convert JavaScript Date object to Moment and format
      selectedDateString = moment(selectedDate).format('YYYY-MM-DD');
    }
  }

  const currentDayItemObject = calendarItems ? calendarItems[selectedDateString] : null;

  // State to track whether we should show the loading spinner
  const [showSpinner, setShowSpinner] = useState(false);
  const [currentDayLastUpdated, setCurrentDayLastUpdated] = useState(null);

  // Base loading state from Redux
  const isLoading = isCalendarItemSaving || isCalendarItemUpdating;

  // Effect to set the last updated timestamp when data loads
  useEffect(() => {
    if (currentDayItemObject?.lastUpdatedTimestamp) {
      const timestamp = currentDayItemObject.lastUpdatedTimestamp;
      // Handle both seconds (10 digits) and milliseconds (13 digits) timestamps
      // If timestamp is in seconds (10 digits), convert to milliseconds
      const timestampInMilliseconds = timestamp.toString().length === 10
        ? timestamp * 1000
        : timestamp;
      setCurrentDayLastUpdated(timestampInMilliseconds);
    } else {
      setCurrentDayLastUpdated(null);
    }
  }, [currentDayItemObject]);

  // Effect to handle the delayed spinner visibility
  useEffect(() => {
    let timeoutId;

    // If loading starts, show spinner immediately
    if (isLoading) {
      setShowSpinner(true);
    } else if (showSpinner) {
      // If loading ends, set a timeout to hide the spinner after 1 second
      timeoutId = setTimeout(() => {
        setShowSpinner(false);
      }, 1000); // Keep spinner for 1 second after loading finishes
    }

    // Clean up timeout if component unmounts or loading state changes again
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading, showSpinner]);

  return (
    <>
      {showSaveIndicator && (
      <BorderedIconWrapper
        data-tooltip-id={currentDayLastUpdated ? uploadTooltipId : undefined}
      >
        <IconContainer>
          <FadingIcon isVisible={showSpinner}>
            <Spinner saving darkTheme />
          </FadingIcon>
          <FadingIcon isVisible={!showSpinner}>
            <IcomoonReact
              iconSet={iconSet}
              size={20}
              icon='cloud-upload'
              color={colors.textSecondary}
            />
          </FadingIcon>
        </IconContainer>
      </BorderedIconWrapper>
      )}

      {showSaveIndicator && currentDayLastUpdated && (
        <ReactTooltip
          id={uploadTooltipId}
          place='left'
          content={`Last updated: ${moment(currentDayLastUpdated).format('MMM D, YYYY h:mm A')}`}
          style={{
            backgroundColor: 'rgba(68, 68, 68, 1)',
            color: colors.white,
            fontFamily: 'Nunito Sans',
            fontSize: '13px',
          }}
        />
      )}
    </>
  );
};

export default CloudUploadIcon;
